import { createClient } from "@jog/ct"
import { CategoryPage, ListerPage, ShopTheLookPage } from "@jog/types"
import { Client } from "@prismicio/client"
import { PrismicDocument } from "@prismicio/types"
import { GetStaticPropsContext } from "next"

import { fetchS3CategoryPage } from "../aws/s3"
import { getPaths, retry } from "../helper"
import { RedisCategoryItem } from "../redis/type"

export const buildCategoryPage = async <
    CategoryDocument extends PrismicDocument,
    ListerDocument extends PrismicDocument,
    ShopTheLookDocument extends PrismicDocument,
>(
    client: Client,
    redisItem: RedisCategoryItem,
    pageContext: GetStaticPropsContext<{ slug: string[] }>
): Promise<CategoryPage<CategoryDocument> | ListerPage<ListerDocument> | ShopTheLookPage<ShopTheLookDocument>> => {
    const { deps, extra, locale, ...others } = redisItem
    const listerData = await retry(() =>
        createClient()
            .category({ id: deps.ct.id, locale })
            .then((res) => res.category)
    )
    if (!listerData) throw new Error("Get ct category fail")
    const paths = getPaths(listerData.custom?.customFieldsRaw)
    if (!paths) throw new Error("Ct category does not have path")
    const title = listerData.description || ""
    const type = deps.prismic?.type || "lister"
    const prismicId = redisItem.deps.prismic?.id
    switch (type) {
        case "shop_the_look": {
            if (!prismicId) throw new Error("Ct category does not have path")
            const prismic = await retry(() =>
                pageContext.preview && pageContext.previewData
                    ? client.getByID<ShopTheLookDocument>(prismicId)
                    : fetchS3CategoryPage<ShopTheLookDocument>("shop_the_look", prismicId)
            )
            return {
                locale,
                ...others,
                categoryInfo: { ...extra, paths, title },
                prismic,
                type: "shopTheLook",
            }
        }
        case "category": {
            if (!prismicId) throw new Error("Ct category does not have path")
            const prismic = await retry(() =>
                pageContext.preview && pageContext.previewData
                    ? client.getByID<CategoryDocument>(prismicId, {
                          fetchLinks: ["category_nav.body"],
                      })
                    : fetchS3CategoryPage<CategoryDocument>("category", prismicId)
            )
            return {
                locale,
                ...others,
                categoryInfo: { ...extra, paths, title },
                prismic,
                type: "category",
            }
        }
        case "lister": {
            const prismic = prismicId
                ? await retry(() =>
                      pageContext.preview && pageContext.previewData
                          ? client.getByID<ListerDocument>(prismicId)
                          : fetchS3CategoryPage<ListerDocument>("lister", prismicId)
                  )
                : null
            const facets = prettyPathParser(pageContext.params!.slug.join("/"))
            return {
                locale,
                ...others,
                categoryInfo: {
                    ...extra,
                    paths,
                    title,
                    facets,
                },
                type: "lister",
                prismic,
            }
        }
        default: {
            throw new Error(`deps.prismic?.type error: ${deps.prismic?.type}`)
        }
    }
}

const prettyPathParser = (prettyPath: string) => {
    const slug = prettyPath.split("/")
    const index = slug.findIndex((i) => i.includes("_"))
    if (index === -1) return {}
    const facets = (prettyPath.match(/[\w-]+?_\S+?(?=(\/\w+_|$))/g) || [])
        .filter((i) => /(.*?)_(.*)/.test(i))
        .map((i) => {
            const [, name, value] = i.match(/(.*?)_(.*)/)!
            return { name, value }
        })
    const facetValues = facets.reduce<Record<string, string>>(
        (pre, cur) => ({ ...pre, [`tn_fk_${cur.name}`]: cur.value.replace(/_/g, " ").replace(/-/g, "|").trim() }),
        {}
    )
    return facetValues
}
